import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, List, ListItemButton, Stack, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { searchPlatform, type PlatformSearchResults } from '../lib/platform-api';
import { useT } from '../i18n';

interface SearchOption {
  key: string;
  category: string;
  title: string;
  detail?: string;
  to: string;
}

const EMPTY: PlatformSearchResults = { developers: [], clusters: [], members: [] };

export function GlobalSearch() {
  const t = useT();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PlatformSearchResults>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(EMPTY); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      searchPlatform(q)
        .then((next) => { if (!cancelled) setResults(next); })
        .catch(() => { if (!cancelled) setResults(EMPTY); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query]);

  const options = useMemo<SearchOption[]>(() => {
    const q = query.trim().toLocaleLowerCase();
    if (q.length < 2) return [];
    const pages = [
      { key: 'overview', title: t('plt.overviewTitle'), to: '/platform/dashboard' },
      { key: 'developers', title: t('plt.devTitle'), to: '/platform/developers' },
      { key: 'clusters', title: t('plt.clusterTitle'), to: '/platform/clusters' },
      { key: 'members', title: t('team.title'), to: '/residents?tab=team' },
      { key: 'archive', title: t('plt.archivedTitle'), to: '/platform/archived' },
    ].filter((page) => page.title.toLocaleLowerCase().includes(q))
      .map((page) => ({ ...page, category: t('search.pages') }));
    return [
      ...pages,
      ...results.developers.map((developer) => ({
        key: `developer-${developer.id}`, category: t('search.developers'), title: developer.name,
        to: `/platform/developers?focus=${developer.id}`,
      })),
      ...results.clusters.map((cluster) => ({
        key: `cluster-${cluster.id}`, category: t('search.clusters'), title: cluster.name,
        detail: cluster.developer_name ?? undefined,
        to: `/platform/clusters?focus=${cluster.id}`,
      })),
      ...results.members.map((member) => ({
        key: `member-${member.id}`, category: t('search.members'), title: member.name,
        detail: `${member.email} · ${member.cluster_name}`,
        to: `/residents?tab=team&developer=${member.developer_id ?? ''}&cluster=${member.cluster_id}&member=${member.id}`,
      })),
    ];
  }, [query, results, t]);

  function pick(option: SearchOption) {
    navigate(option.to);
    setQuery('');
    setMobileOpen(false);
  }

  return (<>
    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
    <Autocomplete<SearchOption, false, false, false>
      size="small"
      options={options}
      groupBy={(option) => option.category}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      filterOptions={(items) => items}
      value={null}
      inputValue={query}
      onInputChange={(_, value) => setQuery(value)}
      onChange={(_, value) => { if (value) pick(value); }}
      loading={loading}
      noOptionsText={query.trim().length < 2 ? t('search.minimum') : t('search.noResults')}
      sx={{ width: { sm: 220, md: 360 } }}
      renderInput={(params) => <TextField {...params} placeholder={t('search.placeholder')}
        slotProps={{ input: { ...params.InputProps, startAdornment: <SearchIcon fontSize="small" color="action" />,
          endAdornment: <>{loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> } }} />}
      renderOption={(props, option) => <Box component="li" {...props} key={option.key}>
        <Stack>
          <Typography variant="body2">{option.title}</Typography>
          {option.detail ? <Typography variant="caption" color="text.secondary">{option.detail}</Typography> : null}
        </Stack>
      </Box>}
    />
    </Box>
    <IconButton color="inherit" onClick={() => setMobileOpen(true)}
      aria-label={t('search.placeholder')} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
      <SearchIcon />
    </IconButton>
    <Dialog open={mobileOpen} onClose={() => setMobileOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>{t('search.placeholder')}</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth size="small" value={query} onChange={(event) => setQuery(event.target.value)}
          placeholder={t('search.placeholder')} />
        {loading ? <CircularProgress size={20} sx={{ m: 2 }} /> : null}
        <List>
          {options.map((option) => <ListItemButton key={option.key} onClick={() => pick(option)}>
            <Stack>
              <Typography variant="body2">{option.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.category}{option.detail ? ` · ${option.detail}` : ''}
              </Typography>
            </Stack>
          </ListItemButton>)}
        </List>
        {!loading && options.length === 0 ? <Typography variant="body2" color="text.secondary">
          {query.trim().length < 2 ? t('search.minimum') : t('search.noResults')}
        </Typography> : null}
      </DialogContent>
    </Dialog>
  </>);
}
